import { Theme } from "react-migration/lib/theme/Theme";

import { FC, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-migration/lib/i18n/useTranslation";
import Feature from "src/js/stores/user/Feature";
import hasFeature from "src/js/stores/user/actions/hasFeature";
import {
  exactMatch,
  gatherConditions,
  PropertyTypeOption,
  rejectInvalidCombinations,
} from "react-migration/domains/comparables/util/PropertyTypes";
import { NestedOptions } from "./NestedOptions";
import {
  PropertyDesignationGQL,
  PropertyCategoryGQL,
  PropertySubCategoryGQL,
} from "react-migration/lib/typings/comparables/Comparables";
import { Accordion } from "react-migration/components/Accordion";
import { DCSCondition } from "react-migration/lib/typings/comparables/dcs";

enum DisabledTextContentColor {
  Light = "text-text-base-primary-disabled",
  Dark = "text-text-base-primary-disabled",
}

export interface FilterPropertyTypesProps {
  disabled?: boolean;
  options: PropertyTypeOption[];
  disabledFields?: Set<HiddenFieldPath>;
  hiddenFields?: Set<HiddenFieldPath>;
  selectedPropTypes: DCSCondition[];
  setSelectedPropTypes: (conditions: DCSCondition[]) => void;
  theme?: Theme;
}

export type HiddenFieldPath =
  | PropertyDesignationGQL // Top level only: "Commercial"
  | `${PropertyDesignationGQL}.${PropertyCategoryGQL}` // Designation.Category: "Commercial.Retail"
  | `${PropertyDesignationGQL}.${PropertyCategoryGQL}.${PropertySubCategoryGQL}`; // Designation.Category.Subcategory: "Commercial.Retail.Shop"

export const FilterPropertyTypes: FC<FilterPropertyTypesProps> = ({
  disabled,
  disabledFields,
  hiddenFields,
  options,
  selectedPropTypes,
  setSelectedPropTypes,
  theme = Theme.Light,
}) => {
  const { t } = useTranslation();

  const optionsFlat = useMemo(() => options.flatMap(gatherConditions), [options]);

  const deselect = useCallback(
    (deselectedConditions: DCSCondition[]) => {
      const nextConditions = selectedPropTypes.filter((activeCondition) =>
        deselectedConditions.every((deselectedCondition) => !exactMatch(deselectedCondition, activeCondition))
      );
      if (nextConditions.length !== selectedPropTypes.length) {
        setSelectedPropTypes(rejectInvalidCombinations(optionsFlat, nextConditions));
      }
    },
    [selectedPropTypes]
  );

  const select = useCallback(
    (selectedConditions: DCSCondition[]) => {
      const filtered = selectedConditions.filter((selectedCondition) =>
        selectedPropTypes.every((activeCondition) => !exactMatch(activeCondition, selectedCondition))
      );
      if (filtered.length) {
        setSelectedPropTypes(
          // TODO: IDK Why this is here? Why do we need to control values when we already
          // control the checkbox values anyway?
          rejectInvalidCombinations(optionsFlat, [...selectedPropTypes, ...filtered])
        );
      }
    },
    [selectedPropTypes]
  );

  const [openedAccordions, setOpenedAccordions] = useState<string[]>([]);
  return (
    <div>
      <h4 className={`mb-2 flex items-center justify-between ${disabled ? DisabledTextContentColor[theme] : ""}`}>
        <span>{t("comparables.map.sidebar.filters.filters.type")}</span>
      </h4>
      <Accordion type="multiple" onValueChange={disabled ? undefined : (value) => setOpenedAccordions(value)}>
        {options.map((option) => {
          const designation = option.designation as PropertyDesignationGQL;
          return (
            <NestedOptions
              disabled={disabled || disabledFields?.has(designation)}
              key={option.category ?? designation}
              deselect={disabled ? undefined : deselect}
              openedAccordions={disabled ? openedAccordions : []}
              option={option}
              selectedOptions={selectedPropTypes}
              select={disabled ? undefined : select}
              hiddenFields={hiddenFields}
              parentPath={designation}
            />
          );
        })}
      </Accordion>
    </div>
  );
};
